<div class="modal-bg">
  <div class="form modal-dialog-container">

    <div class="dialog-header">
      <h2>Help</h2>
      <div class="dialog-close-btn" (click)="closeDialog()">
        <i class="fa fa-times" aria-hidden="true"></i>
      </div>
    </div>

    <div class="dialog-content">
      <div class="help-content">

        @if (getCurrentNavigation() == 'dashboard') {
          <p class="help-intro">This guide covers the main components and tools of the Visible Speech platform.</p>

          <section class="help-section">
            <div class="help-section-header">
              <span class="help-section-icon"><i class="fa fa-th-large" aria-hidden="true"></i></span>
              <h2>Your Projects</h2>
            </div>
            <p>Each project card gives you two panels for working with your data:</p>

            <div class="help-feature-card">
              <div class="help-feature-card-header">
                <i class="fa fa-cubes" aria-hidden="true"></i>
                <h3>Applications</h3>
              </div>
              <p>Tools you can launch to interact with your project's data:</p>
              <ul class="help-list">
                <li><strong>Annotation tool</strong> — Label, annotate, and visualize speech data using Artic.</li>
                <li><strong>Notebook tool</strong> — Run R or Python code in a Jupyter environment on the VISP server to analyze your data using the emuR library.</li>
                <li><strong>Artic</strong> — A standalone transcription editor. No separate login is required, but it operates independently from the rest of the platform.</li>
              </ul>
            </div>

            <div class="help-feature-card">
              <div class="help-feature-card-header">
                <i class="fa fa-cogs" aria-hidden="true"></i>
                <h3>Operations</h3>
              </div>
              <p>Manage your project’s workflow and resources:</p>
              <ul class="help-list">
                <li><strong>Auto-transcribe</strong> — Assign audio files for automatic speech transcription via an AI model.</li>
                <li><strong>Recording sessions</strong> — View and manage recording sessions. Each recording session corresponds to a recording event and contains its associated audio files. Recording sessions can be based on uploaded files, or recorded directly online in VISP.</li>
                <li><strong>Assign annotation tasks</strong> — Distribute annotation bundles to project members. Assignments are tightly integrated with the Transcription tool.</li>
                <li><strong>Members</strong> — Manage your project’s team members and their roles.</li>
                <li><strong>Manage recording scripts</strong> — Create and edit recording scripts. A script is a set of prompts for participants to speak during a recording session.</li>
              </ul>
            </div>
          </section>

          <section class="help-section">
            <div class="help-section-header">
              <span class="help-section-icon"><i class="fa fa-database" aria-hidden="true"></i></span>
              <h2>Data Management</h2>
            </div>
            <p>Your data is securely stored on our servers with Git for version control, providing a full history of changes.</p>
          </section>
        }

        @if (getCurrentNavigation() == 'artic') {
          <section class="help-section">
            <div class="help-section-header">
              <span class="help-section-icon"><i class="fa fa-headphones" aria-hidden="true"></i></span>
              <h2>Artic</h2>
            </div>
            <p>Artic is a rich labeling and correction tool for visualizing, annotating, and analyzing speech data. It supports multiple annotation tiers, spectrogram views, and acoustic measurements.</p>
          </section>

          <section class="help-section">
            <div class="help-section-header">
              <span class="help-section-icon"><i class="fa fa-info-circle" aria-hidden="true"></i></span>
              <h2>Using the tool</h2>
            </div>
            <p>Your assigned bundles appear in the panel on the left. Only bundles assigned to you in the dashboard will be shown — even as project leader you must assign bundles to yourself before they appear here.</p>
            <p>The main area on the right lets you play back audio, view the spectrogram, and edit annotation tiers. For a complete feature reference, see the <a href="https://ips-lmu.github.io/The-EMU-SDMS-Manual/" target="_blank">EMU-SDMS Manual</a>.</p>
            <div class="help-tip">
              <i class="fa fa-lightbulb-o" aria-hidden="true"></i>
              <span>Use <kbd>Tab</kbd> / <kbd>Shift+Tab</kbd> to move between bundles and <kbd>Space</kbd> to play or stop audio.</span>
            </div>
          </section>
        }

        @if (getCurrentNavigation() == 'jupyter') {
          <section class="help-section">
            <div class="help-section-header">
              <span class="help-section-icon"><i class="fa fa-code" aria-hidden="true"></i></span>
              <h2>Jupyter Notebook</h2>
            </div>
            <p>You are running a Jupyter notebook session on the VISP server. Use R or Python to analyze your speech data, including the <strong>emuR</strong> library to work with your Emu-R database — no local installation needed.</p>
          </section>

          <section class="help-section">
            <div class="help-section-header">
              <span class="help-section-icon"><i class="fa fa-info-circle" aria-hidden="true"></i></span>
              <h2>Using the tool</h2>
            </div>
            <p>Write and run code in the notebook editor on the right. Your project directory is visible in the file browser on the left.</p>
            <p><strong>Remember to save your work.</strong> You can navigate back to the dashboard and return to your session later, but idle sessions are periodically shut down — save regularly.</p>
          </section>
        }


        @if (getCurrentNavigation() == 'jupyter') {
          <section class="help-section">
            <div class="help-section-header">
              <span class="help-section-icon"><i class="fa fa-terminal" aria-hidden="true"></i></span>
              <h2>Code examples</h2>
            </div>
            <ul class="help-code-list">

              <li>
                <h3>Load your Emu-R database</h3>
                <div class="code-container">
                  <pre><code id="code1"># Load the emuR library
library(emuR)

# Load your Emu-R database. Within Visible Speech, the database location is assumed to be the same for all projects,
# Within Visible Speech the database path is the same for all projects.
# This gives you a reference called 'VISP' to your speech database.
db_path &lt;- "~/project/Data/VISP_emuDB"
VISP &lt;- load_emuDB(db_path)</code></pre>
                  <button class="copy-btn" (click)="copyCode('code1')">Copy</button>
                </div>
              </li>

              <li>
                <h3>List sessions</h3>
                <div class="code-container">
                  <pre><code id="code2"># List all recording sessions in the database
sessions &lt;- list_sessions(VISP)
print(sessions)</code></pre>
                  <button class="copy-btn" (click)="copyCode('code2')">Copy</button>
                </div>
              </li>

              <li>
                <h3>List bundles</h3>
                <div class="code-container">
                  <pre><code id="code3"># "Bundles" are individual sound files together with their associated signal files.
#”Bundles” is the name used for individual sound files and associated other signal files in a session.
# You can list all bundles in a database

list_bundles(VISP)

# You can also list all bundles or sessions matching a pattern
# In the example below you will get all the bundles of sessions with names (perhaps recording date) starting with 2024

list_bundles(VISP,sessionPattern="2024.*")

# You can always get help for a command using ¨?¨, e.g.

?list_bundles
                        </code></pre>
                        <button class="copy-btn" (click)="copyCode('code3')">Copy</button>
                        </div>
                    </li>

                    <li>
                        <h3>Other usage</h3>
                        <div class="code-container">
                        <pre><code id="code3">
# Anything that is a attribute definition can be queried for labels.
# We here assume that you have a Word level set up, similar to our default setup

query(VISP,"Word !=''") #Provides a list of all non-empty word labels

query(VISP,"Word =~ '^s.*'") # Uses a regular expression to find all words beginning with ‘s’

query(VISP,"Word !=''. -> Word =~ '^s.*'") # Finds all (not empty) word labels preceeding a word starting with ‘s’  

# We can make queries that relate to the link between transcription tiers

# (note the [] braces)

query(VISP,"[Phonetic != '' ^ Word =~ '^s.*']")
                        </code></pre>
                        <button class="copy-btn" (click)="copyCode('code3')">Copy</button>
                        </div>
                    </li>

                </ul>
          </section>
        }


                <!--<span (click)="startTour()">Tutorial</span>-->
            </div>
        </div>

    </div>
</div>
